import React, { useEffect, useRef, useState } from 'react';
import TenantLpImage from "../../../../../../assets/images/landing-page/tenant-lp-image.webp";
import { openExternalLink } from '../../../../../../utils/navigation';
import AndroidDownloadIcon from "../../../../../../assets/images/android_download.png";
import IOSDownloadIcon from "../../../../../../assets/images/ios_download.png";
import { useContentArea } from '../../../../../../context/ContentAreaContext';
import { colorPalette } from '../../../../../../utils/theme';
import { CenterImage, Section, Stage, LeftCard, LeftFloatingBadge, RightCard, RightFloatingBadge, ProgressSection, ProgressTitle, BottomFloatingBadge, StageFooter, FooterBadge, Headline, Subtitle, ButtonContainer, PropertySection, PropertyImage, PropertyInfo, PropertyName, PropertyDates, PaymentSection, PaymentTitle, PaymentAmount, CurrencyText, ProgressBarContainer, ProgressBar, PaymentDueText, PaymentStatusText, CenterImageMobile } from './control-anywhere.styles';
import { ActionArrow, ChecklistContent, ChecklistIcon, ChecklistItem, ChecklistText } from '../../../../../dashboard-page/sub-pages/tenant-dashboard/components/tenant-onboarding-checklist/tenant-onboarding-checklist.styles';
import MaterialIcon from '../../../../../../components/material-icon';
import Tag from '../../../../../../components/tag/tag';
import TheVybeImage from '../../../../../../assets/images/landing-page/homes/the-vybe.webp';
import { formatMoney } from '../../../../../../utils/helpers';
import { useTranslation } from 'react-i18next';
import HeaderMobileEN from "../../../../../../assets/images/landing-page/header_tenants_EN.webp";
import HeaderMobileDE from "../../../../../../assets/images/landing-page/header_tenants_DE.webp";


const ControlAnywhere: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { contentAreaRef } = useContentArea();

  // Refs
  const sectionRef = useRef<HTMLElement>(null);

  // States
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Parallax scroll effect
  useEffect(() => {
    let ticking = false;

    const updateScrollY = () => {
      if (!sectionRef.current || !contentAreaRef.current) return;

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const containerRect = contentAreaRef.current.getBoundingClientRect();
      const containerHeight = containerRect.height;

      // Calculate relative position within the scrollable container
      const relativeTop = sectionRect.top - containerRect.top;
      const relativeBottom = sectionRect.bottom - containerRect.top;

      // Check if section is in viewport of the container
      const isInView = relativeTop < containerHeight && relativeBottom > 0;
      setIsVisible(isInView);

      if (isInView) {
        // Calculate scroll progress based on container viewport
        // When section top is at bottom of container: progress = 0
        // When section top is at top of container: progress = 1
        const scrollProgress = Math.max(0, Math.min(1, (containerHeight - relativeTop) / containerHeight));
        setScrollY(scrollProgress);
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollY);
        ticking = true;
      }
    };

    const scrollContainer = contentAreaRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      updateScrollY(); // Initial call

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [contentAreaRef]);

  // Calculate parallax transforms based on scroll progress
  const getParallaxTransform = (speed: number, direction: 'x' | 'y' = 'y', baseTransform?: string, disableOnMobile: boolean = false) => {
    if (!isVisible) return baseTransform || '';

    // Check if we should disable parallax on mobile
    const isMobile = window.innerWidth <= 767.98;
    if (disableOnMobile && isMobile) return baseTransform || '';

    const movement = scrollY * speed;
    const parallaxTransform = direction === 'x'
      ? `translate3d(${movement}px, 0, 0)`
      : `translate3d(0, ${movement}px, 0)`;

    return baseTransform ? `${baseTransform} ${parallaxTransform}` : parallaxTransform;
  };

  return (
    <Section ref={sectionRef}>
      <Stage>
        <CenterImage
          src={TenantLpImage}
          alt="Tenant App"
          style={{ transform: getParallaxTransform(-15, 'y', 'translateX(-50%)') }}
        />

        {/* Mobile Image */}
        <CenterImageMobile
          src={i18n.language === 'en' ? HeaderMobileEN : HeaderMobileDE}
          alt="Mobile Header"
        />

        <LeftCard style={{ transform: getParallaxTransform(-25, 'x', undefined, true) }}>
          <LeftFloatingBadge style={{ transform: getParallaxTransform(-50, 'y', undefined, true) }}>
            {t('tenant_home.control_anywhere.floating_badges.contract_extended')}
          </LeftFloatingBadge>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <ProgressTitle>{t('tenant_home.control_anywhere.contract_card.title')}</ProgressTitle>
            <Tag
              label={t('tenant_home.control_anywhere.contract_card.status_active')}
              backgroundColor={colorPalette.blue500}
              color={colorPalette.white}
              type='unitStatus'
              variant="rented"
              size="S"
            />
          </div>

          <PropertySection>
            <PropertyImage>
              <img src={TheVybeImage} alt="1611 • The Vybe" />
            </PropertyImage>
            <PropertyInfo>
              <PropertyName>{t('tenant_home.control_anywhere.contract_card.property_name')}</PropertyName>
              <PropertyDates>
                <MaterialIcon icon="calendar_today" size={16} color={colorPalette.textDark} />
                27.08.2025 - 26.08.2026
              </PropertyDates>
            </PropertyInfo>
          </PropertySection>

          <PaymentSection>
            <PaymentTitle>{t('tenant_home.control_anywhere.contract_card.next_rent_payment')}</PaymentTitle>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
              <PaymentAmount>
                <CurrencyText>AED </CurrencyText>
                {formatMoney(20000)}
              </PaymentAmount>
              <div className="d-flex gap-1 align-items-end">
                <PaymentDueText>{t('tenant_home.control_anywhere.contract_card.due_text')}</PaymentDueText>
                <PaymentStatusText color={colorPalette.textMain}>12.09.2025</PaymentStatusText>
              </div>
            </div>

            <ProgressBarContainer>
              <ProgressBar percentage={40} />
            </ProgressBarContainer>
          </PaymentSection>
        </LeftCard>

        <RightCard style={{ transform: getParallaxTransform(30, 'x', undefined, true) }}>
          <RightFloatingBadge style={{ transform: getParallaxTransform(40, 'x', undefined, true) }}>
            {t('tenant_home.control_anywhere.floating_badges.documents_updated')}
          </RightFloatingBadge>
          <ProgressSection>
            <ProgressTitle>{t('tenant_home.control_anywhere.checklist_card.title')}</ProgressTitle>
            <ChecklistItem>
              <ChecklistIcon completed={true} disabled={false}>
                <MaterialIcon icon="check" size={20} color="#24DEA9" />
              </ChecklistIcon>

              <ChecklistContent>
                <ChecklistText completed={true} disabled={false}>
                  {t('tenant_home.control_anywhere.checklist_card.upload_documents')}
                </ChecklistText>
              </ChecklistContent>
            </ChecklistItem>

            <ChecklistItem>
              <ChecklistIcon completed={true} disabled={false}>
                <MaterialIcon icon="check" size={20} color="#24DEA9" />
              </ChecklistIcon>

              <ChecklistContent>
                <ChecklistText completed={true} disabled={false}>
                  {t('tenant_home.control_anywhere.checklist_card.sign_contract')}
                </ChecklistText>
              </ChecklistContent>
            </ChecklistItem>

            <ChecklistItem>
              <ChecklistIcon completed={false} disabled={false}>
                <div className="empty-circle" />
              </ChecklistIcon>

              <ChecklistContent>
                <ChecklistText completed={true} disabled={false}>
                  {t('tenant_home.control_anywhere.checklist_card.submit_checks')}
                </ChecklistText>
              </ChecklistContent>

              <ActionArrow>
                <MaterialIcon icon="chevron_right" size={20} color={colorPalette.blue500} />
              </ActionArrow>
            </ChecklistItem>
          </ProgressSection>
        </RightCard>

        <BottomFloatingBadge style={{ transform: getParallaxTransform(60, 'y', undefined, true) }}>
          {t('tenant_home.control_anywhere.floating_badges.payment_successful')}
        </BottomFloatingBadge>

        <StageFooter>
          <FooterBadge>{t('tenant_home.control_anywhere.footer.badge')}</FooterBadge>
          <Headline>{t('tenant_home.control_anywhere.footer.headline')}</Headline>
          <Subtitle>
            {t('tenant_home.control_anywhere.footer.subtitle').split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index === 0 && <br />}
              </React.Fragment>
            ))}
          </Subtitle>
          <ButtonContainer>
            <div className="d-flex align-items-center gap-2 mt-3">
              <img src={IOSDownloadIcon} alt="Managd Logo" width={126} style={{ cursor: "pointer" }} onClick={() => {
                openExternalLink("https://apps.apple.com/de/app/managd/id6745959836");
              }} />
              <img src={AndroidDownloadIcon} alt="Managd Logo" width={130} style={{ cursor: "pointer" }} onClick={() => {
                openExternalLink("https://play.google.com/store/apps/details?id=com.getmanagd.app");
              }} />
            </div>
          </ButtonContainer>
        </StageFooter>
      </Stage>
    </Section>
  );
};

export default ControlAnywhere;
